/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

/**
 * The `@` picker's logic, with no React and no DOM in it.
 *
 * Everything interesting about a mention picker is arithmetic on a string and a
 * caret position: is the person typing a mention right now, what have they typed
 * so far, and where does the replacement go. Testing that through a rendered
 * textarea would mean simulating keystrokes to check a substring; here it is a
 * function and a literal.
 */

/**
 * What may sit directly before an `@` for it to open a mention.
 *
 * `*` and `~` are on the list because they are what the composer's bold, italic
 * and strike buttons write: pressing **B** and then typing `@` puts an asterisk
 * where a space would be, and without them the picker never opened inside bold
 * text. `server/mentions.js` has the same set — a name drawn as a mention must
 * be one somebody was told about, so the two must not disagree.
 */
const BOUNDARY = /[\s([{"'*~]/;

export interface Person {
  name: string;
  email: string;
}

/**
 * The mention being typed at the caret, if there is one.
 *
 * Walks back from the caret to the nearest `@`, and refuses it when:
 *
 *  - there is no `@` before the caret
 *  - a **newline** intervenes. A mention does not span lines, and without this
 *    an `@` three paragraphs up would keep the menu open for ever
 *  - the `@` is not at a word boundary — `bob@cloudeq.com` is an address
 *    somebody is typing, not a mention, and a menu opening over it is in the way
 *  - more than three words have been typed since. Display names are short; past
 *    that the person is writing a sentence and the menu should get out of it
 */
export function activeMention(
  text: string,
  caret: number,
  people: Person[] = [],
): { query: string; at: number } | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;

  const before = at === 0 ? '' : upto[at - 1];
  if (before && !BOUNDARY.test(before)) return null;

  const query = upto.slice(at + 1);
  if (query.includes('\n')) return null;
  if (query.split(/\s+/).length > 4) return null;

  /**
   * **A finished mention is not an active one.**
   *
   * Choosing a name inserts `@Sean Barker ` and leaves the caret after the
   * space — at which point this used to report a live query of `"Sean Barker "`
   * and the menu reopened over the mention that had just been completed. Found
   * by the test that asserts exactly that, which is the only reason it is not
   * still there.
   *
   * The rule is the honest one rather than a flag the component sets: a query
   * that is a whole name **and has been closed with a space** is done. Typing
   * on then makes it a new query, which is what should reopen the menu.
   */
  if (/\s$/.test(query)) {
    const done = query.trim().toLowerCase();
    if (people.some((p) => p.name.toLowerCase() === done)) return null;
  }

  return { query, at };
}

/**
 * Who matches what has been typed.
 *
 * **An empty query lists everybody**, deliberately: pressing `@` on its own is
 * how somebody finds out who is even in the circle, and an empty menu there
 * looks like the feature is broken. Circles are small enough that the whole
 * roster is a usable list — which is the same reason the directory picker does
 * the opposite, since a few hundred people is not.
 *
 * Matched on the name and on the part of the address before the `@`, because
 * both are things people type.
 */
export function matchPeople(people: Person[], query: string, limit = 6): Person[] {
  const q = query.trim().toLowerCase();
  if (!q) return people.slice(0, limit);

  const starts: Person[] = [];
  const contains: Person[] = [];
  for (const p of people) {
    const name = p.name.toLowerCase();
    // `?? p.email` because `split` is typed as possibly-empty under
    // noUncheckedIndexedAccess; an address with no @ in it is not a real
    // case, and matching the whole string is the right answer if it were.
    const local = (p.email.split('@')[0] ?? p.email).toLowerCase();
    if (name.startsWith(q) || local.startsWith(q)) starts.push(p);
    else if (name.includes(q) || local.includes(q)) contains.push(p);
  }
  // A prefix match first: typing "sea" should offer Sean before it offers
  // "Andrea Seaton", even though both contain the letters.
  return [...starts, ...contains].slice(0, limit);
}

/**
 * Split a message into plain text and the mentions inside it, for rendering.
 *
 * **Matched against the roster, exactly as the server does**, rather than
 * highlighting anything after an `@`. The two must agree: a name shown as a
 * mention that the server did not notify is a promise the app did not keep, and
 * it is worse than showing nothing.
 */
export function splitMentions(text: string, people: Person[]): { text: string; mention: boolean }[] {
  if (!text) return [];
  const roster = [...people].sort((a, b) => b.name.length - a.name.length);
  const out: { text: string; mention: boolean }[] = [];
  let plain = '';

  for (let i = 0; i < text.length; i += 1) {
    const prev = text[i - 1];
    const boundary = i === 0 || (prev !== undefined && BOUNDARY.test(prev));
    if (text[i] === '@' && boundary) {
      const rest = text.slice(i + 1).toLowerCase();
      const hit = roster.find((p) => rest.startsWith(p.name.toLowerCase()));
      if (hit) {
        if (plain) { out.push({ text: plain, mention: false }); plain = ''; }
        out.push({ text: `@${hit.name}`, mention: true });
        i += hit.name.length;
        continue;
      }
    }
    plain += text[i];
  }
  if (plain) out.push({ text: plain, mention: false });
  return out;
}
